/* @flow */
import React from 'react';
import { createRoot } from 'react-dom/client';
import Fab from '@mui/material/Fab';
import { RESIZE_TO_ASSETS, RESIZE_TO_LANDMARKS } from './constants.map';
import { getImage } from '../util/trip_util';
import GetSvgIcon from '../util/svgImage_util';
import mapStyles from './MapsWrapper.module.scss';

/**
 * Normalises whatever the caller holds as "the map" into a google.maps.Map.
 * The shell passes the raw instance, so this is mostly a null guard.
 */
export const getMapInstance = (map: Object) => (map || null);


export const createControlState = () => ({
    showLandmark: false,
    showAsset: false,
    isHideLandmarks: false,
    isHideAssets: false,
    resizeAsset: false,
    resizeLandmark: false,
    isFitBoundRequired: true,
    prevProps: {},
    landmarkCtrl: null,
    assetCtrl: null,
    resizeLandmarkCtrl: null,
    resizeAssetCtrl: null,
    // Dedicated to addGroupResizeIcon (markerGroup/nearestAssetsGroup shapes).
    // Kept separate from resizeLandmarkCtrl/resizeAssetCtrl above, which
    // belong to addClusterControls's own landmark/asset resize buttons - the
    // two features are unrelated and must not share state.
    groupResizeLandmarkCtrl: null,
    groupResizeAssetCtrl: null,
    lastEditableShapeFitType: null,
});

export const resetMapControlState = (map: Object, stateRef: Object) => {
    const instance = getMapInstance(map);
    if (!instance) return;
    const state = stateRef?.current;
    const ownControls = state
        ? [state.landmarkCtrl, state.assetCtrl, state.resizeLandmarkCtrl, state.resizeAssetCtrl,
            state.groupResizeLandmarkCtrl, state.groupResizeAssetCtrl].filter(Boolean)
        : [];
    // Unmount every React root we created so React can tear down the
    // control's internals (listeners, state) instead of the DOM node
    // just being discarded with the map.
    ownControls.forEach((ctrl) => {
        if (ctrl.root) ctrl.root.unmount();
    });
    if (window.google?.maps) {
        // Remove only the specific DOM nodes *this* map instance pushed onto
        // LEFT_BOTTOM. `.clear()` would wipe every control registered at that
        // position - including Google's own native zoom/Street View controls
        // (also configured at LEFT_BOTTOM in MapShell), which aren't
        // automatically re-added afterwards.
        const ownEls = new Set(ownControls.map(ctrl => ctrl.el));
        const arr = instance.controls[window.google.maps.ControlPosition.LEFT_BOTTOM];
        for (let i = arr.getLength() - 1; i >= 0; i -= 1) {
            if (ownEls.has(arr.getAt(i))) arr.removeAt(i);
        }
    }
};

/**
 * Extends `bounds` to cover a route returned by util/map_utils.js (raw
 * Routes API v2 `route` object).
 *
 * Fitting only a route's start and end points under-zooms whenever the road
 * bows away from the straight line between them, which is common on longer
 * trips. Prefer the decoded polyline (the actual driven path, tightest fit)
 * so it composes cleanly with our own `fitBounds` padding; `route.viewport`
 * is Google's own pre-padded display viewport (meant to be fit with no
 * extra padding) and only used as a fallback when no polyline is present.
 *
 * @returns true if anything was added to `bounds`
 */
export const extendBoundsWithRoute = (bounds: Object, route: Object) => {
    if (!bounds || !route) return false;

    const coordinates = route.polyline?.geoJsonLinestring?.coordinates;
    if (Array.isArray(coordinates) && coordinates.length > 0) {
        coordinates.forEach(([lng, lat]) => {
            if (typeof lat === 'number' && typeof lng === 'number') bounds.extend({ lat, lng });
        });
        return true;
    }

    const { viewport } = route;
    if (viewport?.low && viewport?.high
        && typeof viewport.low.latitude === 'number' && typeof viewport.low.longitude === 'number'
        && typeof viewport.high.latitude === 'number' && typeof viewport.high.longitude === 'number') {
        bounds.extend({ lat: viewport.high.latitude, lng: viewport.high.longitude });
        bounds.extend({ lat: viewport.low.latitude, lng: viewport.low.longitude });
        return true;
    }

    return false;
};

export const addLandmarkShowHideButton = (map: Object, props: Object, stateRef: Object) => {
    const instance = getMapInstance(map);
    if (!instance || !window.google) return;
    const state = stateRef.current;

    const handleClick = () => {
        state.isHideLandmarks = !state.isHideLandmarks;
        if (typeof props.hideLandmark === 'function' && props.hideLandmark) {
            props.hideLandmark(state.isHideLandmarks);
        }
    };
    const icon = <GetSvgIcon type="locationIcon" fillcolor={(state.isHideLandmarks) ? 'rgba(0, 0, 0, 0.54)' : '#007aff'} />;
    const button = (
        <Fab
            id="show-landmark"
            className={mapStyles.customControlIconStyle}
            size="small"
            title={state.isHideLandmarks ? 'Show Landmarks' : 'Hide Landmarks'}
            onClick={handleClick}
        >
            {icon}
        </Fab>
    );

    if (!state.showLandmark) {
        state.showLandmark = true;
        const controlDiv = document.createElement('div');
        controlDiv.style.padding = '9px';
        const root = createRoot(controlDiv);
        root.render(button);
        state.landmarkCtrl = { el: controlDiv, root };
        instance.controls[window.google.maps.ControlPosition.LEFT_BOTTOM].push(controlDiv);
    } else if (state.landmarkCtrl) {
        state.landmarkCtrl.root.render(button);
    }
};

export const addGroupResizeIcon = (map: Object, key: string, props: Object, stateRef: Object) => {
    const instance = getMapInstance(map);
    if (!instance || !window.google) return;
    const state = stateRef.current;

    const iconImage = (key === 'landmark') ? getImage('resetToLandmarks') : getImage('resetToVehicles');
    const handleClick = () => {
        state.prevProps.zoom = 0;
        state.isFitBoundRequired = true;
        // zooomToMarkes is defined further down this file; it also calls back
        // into this function, so the two can't both be declared before the
        // other. Safe at runtime since this only runs from a click, after the
        // whole module (including zooomToMarkes) has finished loading.
        // eslint-disable-next-line no-use-before-define
        zooomToMarkes(map, undefined, stateRef);
    };
    const button = (
        <Fab
            className={mapStyles.customControlIconStyle}
            size="small"
            aria-label="Add"
            title={(key === 'landmark') ? RESIZE_TO_LANDMARKS : RESIZE_TO_ASSETS}
            onClick={handleClick}
        >
            <img src={iconImage} alt={`ResetTo${key}`} />
        </Fab>
    );

    // Separate state per key so a map that switches between a landmark
    // markerGroup and an asset markerGroup (or vice versa) over its lifetime
    // gets both buttons created, instead of one shared flag permanently
    // blocking whichever key wasn't first.
    const ctrlKey = (key === 'landmark') ? 'groupResizeLandmarkCtrl' : 'groupResizeAssetCtrl';
    if (!state[ctrlKey]) {
        const controlDiv = document.createElement('div');
        controlDiv.style.padding = '8px';
        const root = createRoot(controlDiv);
        root.render(button);
        state[ctrlKey] = { el: controlDiv, root };
        instance.controls[window.google.maps.ControlPosition.LEFT_BOTTOM].push(controlDiv);
    } else {
        state[ctrlKey].root.render(button);
    }
};

const addClusterControls = (map: Object, props: Object, stateRef: Object) => {
    const instance = getMapInstance(map);
    if (!instance || !window.google) return;
    const state = stateRef.current;

    if (!state.showLandmark && props.appliedFilters) {
        if (props.appliedFilters.hide_landmarks === 'true') state.isHideLandmarks = true;
        if (props.appliedFilters.hide_assets === 'true') state.isHideAssets = true;
    }

    const resizeLandmarkIcon = <GetSvgIcon type="resizeLandmark" fillcolor="#007aff" style={{ height: '44px', width: '44px' }} />;
    const resizeLandmarkButton = (
        <Fab
            id="resize-landmark"
            className={mapStyles.customControlIconStyle}
            size="small"
            title={RESIZE_TO_LANDMARKS}
            onClick={(e) => {
                if (typeof props.resizeLandmark === 'function' && props.resizeLandmark) props.resizeLandmark();
                e.currentTarget.disabled = true;
            }}
        >
            {resizeLandmarkIcon}
        </Fab>
    );
    if (!state.resizeLandmark) {
        state.resizeLandmark = true;
        const resizeLandmarkCtrlDiv = document.createElement('div');
        resizeLandmarkCtrlDiv.style.padding = '9px';
        const root = createRoot(resizeLandmarkCtrlDiv);
        root.render(resizeLandmarkButton);
        state.resizeLandmarkCtrl = { el: resizeLandmarkCtrlDiv, root };
        instance.controls[window.google.maps.ControlPosition.LEFT_BOTTOM]
            .push(resizeLandmarkCtrlDiv);
    } else if (state.resizeLandmarkCtrl) {
        state.resizeLandmarkCtrl.root.render(resizeLandmarkButton);
    }

    const resizeAssetIcon = <GetSvgIcon type="resizeAsset" fillcolor="#007aff" style={{ height: '42px', width: '42px' }} />;
    const resizeAssetButton = (
        <Fab
            id="resize-asset"
            className={mapStyles.customControlIconStyle}
            size="small"
            title={RESIZE_TO_ASSETS}
            onClick={(e) => {
                if (typeof props.resizeAsset === 'function' && props.resizeAsset) props.resizeAsset();
                e.currentTarget.disabled = true;
            }}
        >
            {resizeAssetIcon}
        </Fab>
    );
    if (!state.resizeAsset) {
        state.resizeAsset = true;
        const resizeAssetCtrlDiv = document.createElement('div');
        resizeAssetCtrlDiv.style.padding = '9px';
        const root = createRoot(resizeAssetCtrlDiv);
        root.render(resizeAssetButton);
        state.resizeAssetCtrl = { el: resizeAssetCtrlDiv, root };
        instance.controls[window.google.maps.ControlPosition.LEFT_BOTTOM].push(resizeAssetCtrlDiv);
    } else if (state.resizeAssetCtrl) {
        state.resizeAssetCtrl.root.render(resizeAssetButton);
    }

    addLandmarkShowHideButton(map, props, stateRef);

    const assetIcon = <GetSvgIcon type="asset" fillcolor={(state.isHideAssets) ? 'rgba(0, 0, 0, 0.54)' : '#007aff'} />;
    const assetButton = (
        <Fab
            id="show-asset"
            className={mapStyles.customControlIconStyle}
            size="small"
            title={state.isHideAssets ? 'Show Assets' : 'Hide Assets'}
            onClick={() => {
                state.isHideAssets = !state.isHideAssets;
                if (typeof props.hideAsset === 'function' && props.hideAsset) props.hideAsset(state.isHideAssets);
            }}
        >
            {assetIcon}
        </Fab>
    );
    if (!state.showAsset) {
        state.showAsset = true;
        const assetCtrlDiv = document.createElement('div');
        assetCtrlDiv.style.padding = '9px';
        const root = createRoot(assetCtrlDiv);
        root.render(assetButton);
        state.assetCtrl = { el: assetCtrlDiv, root };
        instance.controls[window.google.maps.ControlPosition.LEFT_BOTTOM].push(assetCtrlDiv);
    } else if (state.assetCtrl) {
        state.assetCtrl.root.render(assetButton);
    }
};

/**
 * Fits the map to whatever shape is currently being displayed, and adds the
 * shape-specific custom controls.
 *
 * @param map    legacy GoogleMap wrapper or raw google.maps.Map
 * @param nextProps current map props; falls back to the last seen props
 * @param stateRef per-map mutable UI state, from createControlState()
 * @param refs   { CircleRef, editableCircleRef, editableRectangleRef, editablePolygonRef }
 */
// Cognitive complexity intentionally high for now; this switch handles every
// map-shape type. Refactoring into per-case functions is tracked separately
// and deliberately out of scope for this change.
export const zooomToMarkes = (
    map: Object, nextProps: Object, stateRef: Object, refs: Object = {},
) => { // NOSONAR
    const instance = getMapInstance(map);
    if (!window.google || !map || !instance || !stateRef) return;

    const state = stateRef.current;

    // Falling back to the last props this *same* map was called with. When the
    // fallback lived in module scope it could hand back another map's props.
    let props = nextProps;
    if (!props) props = state.prevProps;
    else state.prevProps = { ...props };

    if (!props.isGoogleLoaded || !props.markers) return;
    if (typeof props.markers !== 'object' || !props.markers.shape) return;

    const {
        CircleRef, editableCircleRef, editableRectangleRef, editablePolygonRef,
    } = refs;
    const type = props.markers.shape?.type ? props.markers.shape.type.toLowerCase() : '';

    switch (type) {
    case 'markergroup': {
        const { data } = props.markers.shape;
        if (data && data.length > 0 && data[0].latitude !== '' && data[0].latitude !== undefined) {
            const bounds = new window.google.maps.LatLngBounds();
            data.forEach((d) => {
                if ((d.latitude || d.latitude === 0) && (d.longitude || d.longitude === 0) && typeof d.longitude === 'number' && typeof d.latitude === 'number') {
                    bounds.extend({ lat: d.latitude, lng: d.longitude });
                }
            });
            if (((props.zoom || 0) <= 3) && state.isFitBoundRequired) {
                instance.fitBounds(bounds);
                state.isFitBoundRequired = false;
            }
            const { key } = props.markers.shape;
            if (key) addGroupResizeIcon(map, key, props, stateRef);
        }
        break;
    }
    case 'nearestassetsgroup': {
        const { data } = props.markers.shape;
        if (data && data.length > 0 && data[0].latitude !== '' && data[0].latitude !== undefined) {
            const bounds = new window.google.maps.LatLngBounds();
            data.forEach((d) => {
                if ((d.latitude || d.latitude === 0) && (d.longitude || d.longitude === 0) && typeof d.longitude === 'number' && typeof d.latitude === 'number') {
                    bounds.extend({ lat: d.latitude, lng: d.longitude });
                }
            });
            instance.fitBounds(bounds);
            instance.setZoom(instance.getZoom() - 1);
            const { key } = props.markers.shape;
            if (key) addGroupResizeIcon(map, key, props, stateRef);
        }
        break;
    }
    case 'nearestassetlandmark': {
        if (props.markers.asset?.latitude && props.markers.asset?.longitude &&
            props.markers.landmark.markerLat && props.markers.landmark.markerLng) {
            const bounds = new window.google.maps.LatLngBounds();
            const { latitude, longitude } = props.markers.asset;
            bounds.extend({ lat: Number.parseFloat(latitude), lng: Number.parseFloat(longitude) });
            bounds.extend({
                lat: Number.parseFloat(props.markers.landmark.markerLat),
                lng: Number.parseFloat(props.markers.landmark.markerLng),
            });
            // Include the driven route, not just the two endpoints.
            extendBoundsWithRoute(bounds, props.markers.directions);
            // Pixel padding (not a fixed zoom-level offset) so markers/route
            // aren't flush against the map edges - it scales with however
            // tight or wide `bounds` already is, unlike a flat zoom step.
            instance.fitBounds(bounds, 40);
        }
        break;
    }
    case 'marker': {
        const { shape } = props.markers;
        if (shape.lat && shape.lng && typeof shape.lat === 'number' && typeof shape.lng === 'number' && !props.zoom) {
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend({ lat: shape.lat, lng: shape.lng });
            instance.fitBounds(bounds);
        }
        addLandmarkShowHideButton(map, props, stateRef);
        break;
    }
    case 'batch_directions': {
        const { events } = props.markers;
        if (props.markers.zoomEvent?.locationSegment) {
            const { lat, lng } = props.markers.zoomEvent.locationSegment;
            instance.panTo(new window.google.maps.LatLng(lat, lng));
        } else if (events && events.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            events.forEach((evt) => {
                bounds.extend({
                    lat: evt.locationSegment.lat,
                    lng: evt.locationSegment.lng,
                });
            });
            instance.fitBounds(bounds);
        }
        addLandmarkShowHideButton(map, props, stateRef);
        break;
    }
    case 'polygon': {
        if (props.markers.zoomEvent?.locationSegment) {
            const { lat, lng } = props.markers.zoomEvent.locationSegment;
            instance.panTo(new window.google.maps.LatLng(lat, lng));
        } else if (props.markers.shape.points
            && Array.isArray(props.markers.shape.points) && !props.zoom) {
            const bounds = new window.google.maps.LatLngBounds();
            props.markers.shape.points.forEach((d) => {
                if (d.lat && d.lng && typeof d.lng === 'number' && typeof d.lat === 'number') {
                    bounds.extend({ lat: d.lat, lng: d.lng });
                }
            });
            instance.fitBounds(bounds);
        }
        break;
    }
    case 'rectangle': {
        const { shape } = props.markers;
        if (shape.point1 && shape.point2 &&
            shape.point1.lat && shape.point1.lng &&
            typeof shape.point1.lat === 'number' && typeof shape.point1.lng === 'number' &&
            shape.point2.lat && shape.point2.lng &&
            typeof shape.point2.lat === 'number' && typeof shape.point2.lng === 'number' &&
            !props.zoom) {
            const sw = new window.google.maps.LatLng(shape.point1.lat, shape.point1.lng);
            const ne = new window.google.maps.LatLng(shape.point2.lat, shape.point2.lng);
            instance.fitBounds(new window.google.maps.LatLngBounds(sw, ne));
        }
        break;
    }
    case 'circle': {
        if (CircleRef?.current && typeof CircleRef.current.getBounds === 'function' && CircleRef.current.getBounds() && !props.zoom) {
            instance.fitBounds(CircleRef.current.getBounds());
        }
        break;
    }
    case 'editablecircle': {
        if ((state.lastEditableShapeFitType !== type)
            && editableCircleRef?.current?.CircleRef?.current
            && typeof editableCircleRef.current.CircleRef.current.getBounds === 'function'
            && editableCircleRef.current.CircleRef.current.getBounds()) {
            instance.fitBounds(editableCircleRef.current.CircleRef.current.getBounds());
            state.lastEditableShapeFitType = type;
        }
        break;
    }
    case 'editablerectangle': {
        if ((state.lastEditableShapeFitType !== type)
            && editableRectangleRef?.current?.RectangleRef?.current
            && typeof editableRectangleRef.current.RectangleRef.current.getBounds === 'function'
            && editableRectangleRef.current.RectangleRef.current.getBounds()) {
            instance.fitBounds(editableRectangleRef.current.RectangleRef.current.getBounds());
            state.lastEditableShapeFitType = type;
        }
        break;
    }
    case 'editablepolygon': {
        if ((state.lastEditableShapeFitType !== type)
            && editablePolygonRef?.current?.PolygonRef?.current
            && typeof editablePolygonRef.current.PolygonRef.current.getPath === 'function'
            && editablePolygonRef.current.PolygonRef.current.getPath()) {
            const bounds = new window.google.maps.LatLngBounds();
            const paths = editablePolygonRef.current.PolygonRef.current.getPath();
            for (let i = 0; i < paths.getLength(); i += 1) {
                const xy = paths.getAt(i);
                bounds.extend({ lat: xy.lat(), lng: xy.lng() });
            }
            instance.fitBounds(bounds);
            state.lastEditableShapeFitType = type;
        }
        break;
    }
    case 'polyline': {
        const { shape } = props.markers;
        if (props.markers.zoomEvent?.locationSegment) {
            const { lat, lng } = props.markers.zoomEvent.locationSegment;
            instance.panTo(new window.google.maps.LatLng(lat, lng));
        } else if (shape.data && Array.isArray(shape.data) && shape.data.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            shape.data.forEach((d) => {
                d.events.forEach((e) => {
                    if (e.locationSegment?.lng && e.locationSegment?.lat &&
                        typeof e.locationSegment.lng === 'number' && typeof e.locationSegment.lat === 'number') {
                        bounds.extend({ lat: e.locationSegment.lat, lng: e.locationSegment.lng });
                    }
                });
            });
            instance.fitBounds(bounds);
        }
        addLandmarkShowHideButton(map, props, stateRef);
        break;
    }
    case 'cluster': {
        addClusterControls(map, props, stateRef);
        break;
    }
    default: break;
    }
};
